import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { usePageSize } from '../../hooks/usePageSize';
import { useNavigate } from 'react-router-dom';
import { CircularProgress, Tooltip } from '@mui/material';
import { TransactionMapModel, RequestResult, GetTransactionMapInput, OrderDirection, TransactionMapOrderByInput, BlobDocumentType } from '../../gql-types.generated';
import { selectMapList, selectError, clearError, clearState, selectDeleteMapStatus, selectAddMapStatus, captureAddMapStatus, captureDeleteMapStatus, selectRequestsInFlight, selectMapPagingResult } from './MapsSlice';
import { fetchMapList, deleteMapData, uploadNewMap } from './MapsActions';
import { MainContentBox, DataGridListScrollBox, PageTitleToolbarGrid, MainDataGrid } from '../../util/SharedStyles';
import { useTitle } from '../../util/Common';
import { setToastConfig } from '../EDIContainer/EDIContainerSlice';
import { Viewer, ToastSeverity } from '../../util/Constants';
import { viewerCanEdit } from '../../util/ViewerUtility';
import { useBlobStorageDownload } from '../../hooks/useBlobStorageDownload';
import { getShortDateString, getTimeString } from "../../util/DateTimeUtility";
import { DateTime } from 'luxon';

import PageTitleBar from '../../components/PageTitleBar';
import ErrorMessage from '../../components/ErrorMessage';
import NoRecordsMessage from '../../components/NoRecordsMessage';
import CreateNewButton from '../../components/buttons/CreateNewButton';
import AddMapDialog from '../../components/dialogs/AddMapDialog';
import DeleteDialog from '../../components/dialogs/DeleteDialog';
import FiltersButton from '../../components/buttons/FiltersButton';
import MapsFilterBar from '../../components/filters/MapsFilterBar';
import GridCellDualVert from "../../components/listItems/GridCellDualVert";
import GridListFooter from '../../components/paging/GridListFooter';
import MainDataGridLoadingSkeleton from '../../components/MainDataGridLoadingSkeleton';
import { GridActionsCellItem, GridColumns, GridRenderCellParams, GridRowModel, GridOverlay, GridSortItem, GridSortModel, GridRowParams, useGridApiContext } from '@mui/x-data-grid-pro';
import DownloadIcon from "@mui/icons-material/Download";
import AddIcon from "@mui/icons-material/Add";

interface MapsProps {
    viewer: Viewer | undefined;
}

const Maps: React.FC<MapsProps> = (props) => {
    const { viewer } = props;
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [openModify, setOpenModify] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [deleteErrorMessage, setDeleteErrorMessage] = useState('');
    const [selectedMap, setSelectedMap] = useState<undefined | TransactionMapModel>(undefined);
    const [contentAreaHeight, setContentAreaHeight] = useState('auto');
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [filtersCleared, setFiltersCleared] = useState(false);
    const [filterVersion, setFilterVersion] = useState<string | undefined>(undefined);
    const [filterDescription, setFilterDescription] = useState<string | undefined>(undefined);
    const [filterTransactionIds, setFilterTransactionIds] = useState<string[]>([]);
    const [filterFileName, setFilterFileName] = useState<string | undefined>(undefined);
    const [filterDateFrom, setFilterDateFrom] = useState<DateTime | undefined>(undefined);
    const [filterDateTo, setFilterDateTo] = useState<DateTime | undefined>(undefined);
    const [filterCount, setFilterCount] = useState(0);
    const [mapRows, setMapRows] = useState<GridRowModel[] | undefined>(undefined);
    const [sortFirstLoad, setSortFirstLoad] = useState(true);
    
    const [sortModel, setSortModel] = useState<GridSortModel>([
        {
            field: 'lastModifiedTime',
            sort: 'desc',
        },
    ]);
    const [serverSortModel, setServerSortModel] = useState<TransactionMapOrderByInput[] | undefined>([{
        lastModifiedTime: OrderDirection.Desc
    } as TransactionMapOrderByInput]);

    const [pageSize, handlePageSizeChange] = usePageSize('maps');
    const [totalCount, setTotalCount] = useState(0);

    const deleteMapStatus = useAppSelector(selectDeleteMapStatus);
    const addMapStatus = useAppSelector(selectAddMapStatus);
    const error = useAppSelector(selectError);
    const requestsInFlight = useAppSelector(selectRequestsInFlight);
    const mapPagingResult = useAppSelector(selectMapPagingResult);
    const maps = useAppSelector(selectMapList);

    const { downloadBlob, isDownloading } = useBlobStorageDownload();
    const canEdit = viewerCanEdit(viewer);

    useTitle("Maps");

    useEffect(() => {
        // fetch maps list when a successful mutation occurs
        if (addMapStatus?.result === RequestResult.Success) {

            // navigate to the new map if it was just added
            if (selectedMap === undefined) {
                // if a mapId was successfully returned, navigate 
                // to the new map details to allow user to 
                // add partners right away
                if (addMapStatus.transactionMapId) {
                    // simulate a card click to navigate
                    rowClickAction(addMapStatus.transactionMapId);
                }
            }

            // close the modify dialog
            onMapDialogClose();

            dispatch(setToastConfig({
                message: addMapStatus.message as string,
                severity: ToastSeverity.Success
            }));

            // remove upsert status
            dispatch(captureAddMapStatus());
        }
    }, [addMapStatus?.result]);

    useEffect(() => {
        if (deleteMapStatus?.result === RequestResult.Success) {
            // close the delete dialog
            onDeleteDialogClose();
            dispatch(setToastConfig({
                message: deleteMapStatus.message as string,
                severity: ToastSeverity.Success
            }));
        }
        else if (deleteMapStatus?.result === RequestResult.Fail) {
            setDeleteErrorMessage(deleteMapStatus.message as string);
        }
    }, [deleteMapStatus?.result]);

    useEffect(() => {
        // we have content, so lets properly size that content area        
        const titleHeight = document.getElementById('maps-title-comp')?.clientHeight || 0;
        // if filters are open, include that in the calculation
        let filterBarHeight = 0;
        if (filtersOpen) {
            filterBarHeight = document.getElementById('maps-filter-bar')?.clientHeight || 0;
        }
        let totalHeaderAreaHeight = titleHeight + filterBarHeight;
        if (totalHeaderAreaHeight > 0)
            setContentAreaHeight(`calc(100% - ${totalHeaderAreaHeight}px)`);
    }, [maps, filtersOpen]);

    useEffect(() => {
        let numFilters = 0;
        if (filterVersion && filterVersion.length > 0) {
            numFilters += 1;
        }
        if (filterDescription && filterDescription.length > 0) {
            numFilters += 1;
        }
        if (filterTransactionIds && filterTransactionIds.length > 0) {
            numFilters += 1;
        }
        if (filterFileName && filterFileName.length > 0) {
            numFilters += 1;
        }
        if (filterDateFrom) {
            numFilters += 1;
        }
        if (filterDateTo) {
            numFilters += 1;
        }
        setFilterCount(numFilters);       
    }, [filterVersion, filterDescription, filterTransactionIds, filterFileName, filterDateFrom, filterDateTo]);

    useEffect(() => {
        setMapRows(getMapRows());
        if (!maps && requestsInFlight > 0) {
            setIsLoading(true);
        } else if (requestsInFlight > 0) {
            setIsLoading(true);
        } else {
            setIsLoading(false);
        }
    }, [maps, requestsInFlight]);

    const getRefreshInput = (endEdge: string | undefined, filterStorageFileName: string | undefined, filterDesc: string | undefined, filterTranIds: string[], filterFromDate?: DateTime, filterToDate?: DateTime, filterStandardVersion?: string) => {
        return {
            after: endEdge,
            limit: pageSize,
            description: filterDesc,
            fileName: filterStorageFileName,
            transactionIds: filterTranIds,
            dateFrom: filterFromDate?.toISO(),
            dateTo: filterToDate?.toISO(),
            standardVersion: filterStandardVersion,
            order: serverSortModel
        } as GetTransactionMapInput
    }

    useEffect(() => {
        if (!sortFirstLoad) {
            const sortRefreshData = getRefreshInput(undefined, filterFileName, filterDescription, filterTransactionIds, filterDateFrom, filterDateTo, filterVersion);
            onServerSortModelChanged(sortRefreshData);
        } else {
            setSortFirstLoad(false);
        }
    }, [serverSortModel]);

    useEffect(() => {
        if (mapPagingResult?.totalCount) {
            setTotalCount(mapPagingResult.totalCount);
        }
    }, [mapPagingResult?.totalCount]);

    useEffect(() => {
        if (totalCount > 0) {
            // Refresh list for new page size
            loadPage(undefined);
        }
    }, [pageSize]);

    const loadPage = (endEdge: string | undefined) => {
        let input = getRefreshInput(endEdge, filterFileName, filterDescription, filterTransactionIds, filterDateFrom, filterDateTo, filterVersion);
        dispatch(fetchMapList(input));
    };

    const handlePageLoad = () => {
        if (!mapPagingResult) {
            return;
        }
        if (!mapPagingResult.cursor?.nextPage) {
            return;
        }

        loadPage(mapPagingResult.cursor.nextPage);
    };

    const rowClickAction = (id: string | undefined) => {
        if (id) {
            let route = "/transactionmap/" + id;
            navigate(route);
        }
    };

    const handleClearError = () => {
        dispatch(clearError());
    };

    const onAddMapClick = () => {
        // Clear error and open dialog
        handleClearError();
        // ensure no previously selected standard is set
        if (selectedMap) {
            setSelectedMap(undefined);
        }
        setOpenModify(true);
    };

    const onMapDialogClose = () => {
        setOpenModify(false);
        onDialogClose();
    };

    const onDeleteDialogClose = () => {
        setOpenDelete(false);
        onDialogClose();
        dispatch(captureDeleteMapStatus());
        setDeleteErrorMessage('');
    };

    const onDialogClose = () => {
        // Clear error and selectedMap on close.
        handleClearError();
        setSelectedMap(undefined);
        // Refresh list to bring in potential updates
        loadPage(undefined);
    };

    const onMapDialogSave = (transactionId: string, description: string, documentString?: string, sourceFileName?: string, versionComment?: string, standardVersion?: string) => {
        // upload to save data
        dispatch(uploadNewMap({
            transactionId,
            description,
            documentString,
            sourceFileName,
            versionComment,
            standardVersion
        }));
    };

    const onDeleteDialogConfirm = (id: string) => {
        // delete the selected map
        dispatch(deleteMapData(id));
    }

    const onFiltersClick = () => {
        // Show/hide filters bar
        setFiltersOpen(!filtersOpen);
        // reset filters cleared to false since not clicking close button on bar at this point
        setFiltersCleared(false);
    };

    const onFilterBarClose = () => {
        // set filters as cleared
        setFiltersCleared(true);
        setFilterCount(0);
        // hide filter bar
        setFiltersOpen(false);
    };

    const refreshFilters = (filterVrsn: string | undefined, filterDesc: string | undefined, filterTranIds: string[], fileName: string | undefined, dateFrom: DateTime | undefined, dateTo: DateTime | undefined) => {
        setFilterVersion(filterVrsn);
        setFilterDescription(filterDesc);
        setFilterTransactionIds(filterTranIds);
        setFilterFileName(fileName);
        setFilterDateFrom(dateFrom);
        setFilterDateTo(dateTo);
        dispatch(clearState());
        let input = getRefreshInput(undefined, fileName, filterDesc, filterTranIds, dateFrom, dateTo, filterVrsn)
        dispatch(fetchMapList(input));
    }

    const getServerSortEntryFromGridSortEntry = (entry: GridSortItem) => {
        let newModel = {} as TransactionMapOrderByInput;
        switch (entry.field) {
            case 'lastModifiedTime':
                newModel.lastModifiedTime = entry.sort === 'asc' ? OrderDirection.Asc : OrderDirection.Desc;
                break;
            case 'fileName':
                newModel.fileName = entry.sort === 'asc' ? OrderDirection.Asc : OrderDirection.Desc;
                break;
            case 'transactionName':
                newModel.transactionName = entry.sort === 'asc' ? OrderDirection.Asc : OrderDirection.Desc;
                break;
        }
        return newModel;
    };

    const createdAndSetServerSortModel = (model: GridSortModel) => {
        if (model && model.length > 0) {
            let newArray = [] as TransactionMapOrderByInput[];
            let i = 0;
            for (i; i < model.length; ++i) {
                newArray.push(getServerSortEntryFromGridSortEntry(model[i]));
            }
            setServerSortModel(newArray);
        } else {
            setServerSortModel(undefined);
        }
    };

    const onSortModelChange = (model: GridSortModel) => {
        createdAndSetServerSortModel(model);
        setSortModel(model);
    };

    const onServerSortModelChanged = (refreshInput: GetTransactionMapInput) => {
        dispatch(clearState());
        dispatch(fetchMapList(refreshInput));
    };

    const getSelectedRow = useCallback((rowId: string) => {
        if (rowId && maps?.length) {
            let map = maps.find(m => m.id === rowId);
            return map;
        }
        return undefined;
    }, [maps]);

    const editHandler = useCallback((rowId: string) => {
        let map = getSelectedRow(rowId);
        if (map) {
            setSelectedMap(map);
            setOpenModify(true);
        }
    }, [getSelectedRow]);

    const downloadHandler = useCallback((event: React.MouseEvent<any>, blobFileName: string, blobDocumentId: string) => {
        // called from inside the grid with a row click, so stop the click event from bubbling up
        event.stopPropagation();
        if (blobDocumentId?.length && blobFileName?.length) {
            downloadBlob(blobDocumentId, blobFileName, BlobDocumentType.TransactionMap);
        }
    }, [downloadBlob]);

    const getMapRows = () => {
        if (maps && maps.length > 0) {
            return maps.map((map: TransactionMapModel) => {
                const { id, description, standardVersion, totalPartners, transaction } = map;
                const activeBlobStorage = map.activeVersionBlobStorageReference?.blobStorage;
                const { blobFileName, fileName, lastModifiedByName, lastModifiedTime, version } = activeBlobStorage || {};
                const blobDocumentId = map.activeVersionBlobStorageReference?.blobDocumentId;
                
                return {
                    _raw: map,
                    id,
                    fileName,
                    description, 
                    version,
                    transactionName: transaction?.name,
                    standardVersion,
                    totalPartners: totalPartners || undefined,
                    lastModifiedByName, 
                    lastModifiedTime,
                    blobDocumentId,
                    blobFileName
                }

            }) as GridRowModel[];
        }
    };

    const mapColumns = useMemo<GridColumns<GridRowModel>>(
        () => [
            {
                headerName: 'FILE NAME',
                field: 'fileName',
                flex: 2,
                sortable: true,
                cellClassName: "ediDataGridCellFirstChild",
            },
            {
                headerName: 'DESCRIPTION',
                field: 'description',
                flex: 2,
                sortable: false,
                cellClassName: 'ediDataGridLongDescriptionCell',
            },
            {
                headerName: 'ACTIVE VERSION',
                field: 'version',
                flex: 1,
                sortable: false,
            },
            {
                headerName: 'TRANSACTION',
                field: 'transactionName',
                flex: 1.5,
                sortable: true,
            },
            {
                headerName: 'EDI STANDARD VERSION',
                field: 'standardVersion',
                flex: 1.5,
                sortable: false,
            },
            {
                headerName: '# PARTNERS',
                field: 'totalPartners',
                flex: 1,
                sortable: false,
                type: 'number',
            },
            {
                headerName: 'LAST MODIFIED',
                field: 'lastModifiedTime',
                flex: 1.5,
                sortable: true,
                type: 'dateTime',
                renderCell: (params: GridRenderCellParams) => {
                    const { value } = params;
                    let dateValue = value ? getShortDateString(value) : undefined;
                    let timeValue = value ? getTimeString(value, { includeSeconds: true }) : undefined;
                    return (
                        <GridCellDualVert header={dateValue} sub={timeValue} />
                    )
                },
            },
            {
                headerName: 'LAST MODIFIED BY',
                field: 'lastModifiedByName',
                flex: 1.5,
                sortable: false,
            },
            {
                field: 'id',
                headerName: 'FILE',
                minWidth: 80,
                headerAlign: 'center',
                align: 'center',
                renderCell: (params: GridRenderCellParams) => {
                    const { row } = params;
                    const { blobFileName, blobDocumentId } = row;

                    return (
                        <GridActionsCellItem
                            label="Download"
                            color="primary"
                            onClick={(event: React.MouseEvent<any>) => downloadHandler(event, blobFileName, blobDocumentId)}
                            icon={<Tooltip title="Download"><DownloadIcon /></Tooltip>}
                        />
                    );
                }
            },
            {
                field: 'actions',
                type: 'actions',
                sortable: false,
                resizable: false,
                minWidth: 100,
                headerAlign: 'center',
                align: 'right',
                hide: true, // hide always for now !canEdit
                cellClassName: "ediDataGridCellLastChild",
                // eslint-disable-next-line react/display-name
                getActions: (params: GridRowParams<GridRowModel>) => [
                    <GridActionsCellItem
                        icon={<Tooltip title="Add File Version"><AddIcon /></Tooltip>}
                        label="Edit"
                        color="primary"
                        onClick={() => editHandler(params.row.id)}
                        showInMenu={false}
                    />
                ],
            },
        ], [editHandler, downloadHandler]
    )

    const onRowClickHandler = (params: GridRowParams<GridRowModel>) => {
        if (params && params.row?.id) {
            rowClickAction(params.row.id);
        }
    };

    const GetApiRef = () => {
        return useGridApiContext();
    };

    const circularProgress = <CircularProgress
                aria-label={'progress spinner'}
                key={'maps-spinner'} 
                size={48}
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    marginTop: '-24px',
                    marginLeft: '-24px',
                    zIndex: 1500
                }}
            />;

    const loadingOverlay = () => {
        return (
            <GridOverlay>
                {error && (
                    <ErrorMessage title='Unable to load the Transaction Maps' error={error}></ErrorMessage>
                )}
                {!error && (maps && maps.length > 0 && requestsInFlight > 0) && (
                    circularProgress
                )}
                {!error && (!maps || maps.length === 0) && requestsInFlight > 0 && (
                    <MainDataGridLoadingSkeleton apiRef={GetApiRef()} rowBottomMargin={6} />
                )}
            </GridOverlay>
        );
    };

    const noRowsOverlay = () => {
        return (
            <GridOverlay>
                {error && (
                    <ErrorMessage title='Unable to load the Transaction Maps' error={error}></ErrorMessage>
                )}
                {!error && (maps && maps.length <= 0) && (
                    <NoRecordsMessage topMargin={6} message="" />
                )}
            </GridOverlay>
        );
    };

    const customFooter = () => {
        return (
            <GridListFooter
                pageSize={pageSize}
                onPageSizeChange={(newPageSize: number) => {
                    handlePageSizeChange(newPageSize);
                }}
            />
        );
    };

    return (
        <MainContentBox>
            <PageTitleBar className="pagingbox-titlebar" text='Transaction Maps' id="maps-title-comp">
                <PageTitleToolbarGrid item>
                    <FiltersButton
                        onClick={onFiltersClick}
                        filterCount={filterCount}
                        filtersCleared={filtersCleared}
                        disabled={isLoading}
                        aria-label="filter button"
                        data-cy="map-filters"
                    />
                    {canEdit &&
                        <CreateNewButton
                            text="New Map"
                            onClick={onAddMapClick}
                            data-cy="add-new-map"
                        />}
                </PageTitleToolbarGrid>
            </PageTitleBar>
            <MapsFilterBar
                id="maps-filter-bar"
                className="pagingbox-filterbar"
                visible={filtersOpen}
                loading={isLoading}
                viewer={viewer}
                onFilterChanged={refreshFilters}
                onClose={onFilterBarClose}
            />
            <DataGridListScrollBox scrollheight={contentAreaHeight}>
                <MainDataGrid
                    loading={isLoading}
                    headerHeight={38}
                    rowHeight={52}
                    aria-label="Transaction Map List"
                    rowCount={totalCount}
                    hideFooterSelectedRowCount
                    disableColumnMenu
                    disableColumnFilter
                    disableSelectionOnClick
                    onRowClick={onRowClickHandler}
                    rows={mapRows ?? []}
                    columns={mapColumns}
                    sortingOrder={['asc', 'desc']}
                    sortingMode="server"
                    sortModel={sortModel}
                    onSortModelChange={onSortModelChange}
                    onRowsScrollEnd={handlePageLoad}
                    components={{
                        // eslint-disable-next-line react/display-name,@typescript-eslint/naming-convention
                        LoadingOverlay: loadingOverlay,
                        // eslint-disable-next-line react/display-name,@typescript-eslint/naming-convention
                        NoRowsOverlay: noRowsOverlay,
                        // eslint-disable-next-line react/display-name,@typescript-eslint/naming-convention
                        Footer: customFooter
                    }}
                />
            </DataGridListScrollBox>
            <AddMapDialog
                isOpen={openModify}
                map={selectedMap}
                onClose={onMapDialogClose}
                onSave={onMapDialogSave}
                error={error}
            />
            {/* <DeleteDialog
                isOpen={openDelete}
                id={selectedMap?.id}
                heading={'Delete Transaction Map'}
                message={'Are you sure you want to delete \'' + selectedMap?.description + '\'?'}
                onConfirm={onDeleteDialogConfirm}
                onReject={onDeleteDialogClose}
                errorMessage={deleteErrorMessage}
            /> */}
            {(isDownloading) &&
                circularProgress
            }
        </MainContentBox>
    );
};

export default Maps;
